import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { Avatar, EmptyState, Pill } from "../primitives";
import { Panel, PanelRow } from "./Panel";
import { getAgentPersona } from "./CockpitHeader";
import { useListAgentTasks } from "../../hooks/use-list-agent-tasks";
import { useListTree, useWorkspacePages } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import { pageSurfaceMeta } from "../../lib/page-meta";
import type { LibraryNode } from "../../../lib/types";
import { VerificationBadge } from "../verification/VerificationBadge";

interface ActiveWorkPanelProps {
  /** Workspace root node id — tasks are queried subtree-wide from here. */
  rootNodeId: string;
}

const MAX_VISIBLE = 8;

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// A unified work item — an in-progress agent task or an in-progress page.
type WorkItem =
  | { kind: "task"; id: string; title: string; nodeId: string; updatedAt: string }
  | { kind: "page"; id: string; title: string; surface: string | null; updatedAt: string; frontmatter?: Record<string, unknown> | null };

/**
 * Active work (Sprint 5n + 5o). Surfaces everything in flight across the
 * workspace: agent tasks with status `in_progress` AND pages with status
 * `in-progress`, merged and ordered by recency. The 5o hook lights up here.
 */
export function ActiveWorkPanel({ rootNodeId }: ActiveWorkPanelProps) {
  const { currentWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const tasks = useListAgentTasks({ nodeId: rootNodeId, status: "queued" });
  const pages = useWorkspacePages();
  const tree = useListTree(currentWorkspaceId ?? undefined);

  const agentById = useMemo(
    () => new Map((tree.data ?? []).map((n) => [n.id, n])),
    [tree.data],
  );

  const items = useMemo<WorkItem[]>(() => {
    const taskItems: WorkItem[] = (tasks.data ?? []).map((t) => ({
      kind: "task",
      id: t.id,
      title: t.title,
      nodeId: t.agent_id ?? "",
      updatedAt: t.updated_at,
    }));
    const pageItems: WorkItem[] = (pages.data ?? [])
      .filter((p) => p.status === "in-progress")
      .map((p) => ({
        kind: "page",
        id: p.id,
        title: p.name,
        surface: p.surface,
        updatedAt: p.updated_at,
        frontmatter: p.frontmatter,
      }));
    return [...taskItems, ...pageItems].sort(
      (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    );
  }, [tasks.data, pages.data]);

  const visible = items.slice(0, MAX_VISIBLE);

  return (
    <Panel
      title="Active work"
      count={items.length}
      isLoading={tasks.isLoading || pages.isLoading}
      error={tasks.error ?? pages.error}
      onRetry={() => { tasks.refetch(); pages.refetch(); }}
      isEmpty={items.length === 0}
      empty={<EmptyState>No active work. Tasks and pages in progress will appear here.</EmptyState>}
      footer={
        items.length > MAX_VISIBLE ? (
          <div style={showAllStyle} title="A dedicated work view is coming in a later sprint.">
            Show all ({items.length})
          </div>
        ) : null
      }
    >
      {visible.map((item) =>
        item.kind === "task" ? (
          <TaskRow key={`task:${item.id}`} item={item} agent={agentById.get(item.nodeId)} onClick={() => navigate(`/agents/${item.nodeId}`)} />
        ) : (
          <PageWorkRow key={`page:${item.id}`} item={item} onClick={() => navigate(`/library/${item.id}`)} />
        ),
      )}
    </Panel>
  );
}

function TaskRow({
  item,
  agent,
  onClick,
}: {
  item: Extract<WorkItem, { kind: "task" }>;
  agent: LibraryNode | undefined;
  onClick: () => void;
}) {
  const agentName = agent?.name ?? "Unknown agent";
  return (
    <PanelRow onClick={onClick} title={item.title} trailing={<OpenArrow />}>
      <i className="ti ti-clipboard-list" style={kindIconStyle} aria-hidden="true" title="Task" />
      <span style={titleStyle}>{item.title}</span>
      {agent ? <Avatar persona={getAgentPersona(agent)} size="xs" name={agentName} title={agentName} /> : null}
      <span style={metaStyle}>{agentName}</span>
      <span style={timeStyle}>{formatRelativeTime(item.updatedAt)}</span>
    </PanelRow>
  );
}

function PageWorkRow({
  item,
  onClick,
}: {
  item: Extract<WorkItem, { kind: "page" }>;
  onClick: () => void;
}) {
  const surface = pageSurfaceMeta(item.surface);
  return (
    <PanelRow onClick={onClick} title={item.title} trailing={<OpenArrow />}>
      <i className="ti ti-file-text" style={kindIconStyle} aria-hidden="true" title="Page" />
      <span style={titleStyle}>{item.title}</span>
      <Pill variant={surface.variant}>{surface.label}</Pill>
      <VerificationBadge frontmatter={item.frontmatter} warningOnly />
      <span style={timeStyle}>{formatRelativeTime(item.updatedAt)}</span>
    </PanelRow>
  );
}

function OpenArrow() {
  return (
    <span style={openStyle} aria-hidden="true">
      <i className="ti ti-arrow-right" style={{ fontSize: 13 }} />
    </span>
  );
}

const kindIconStyle: CSSProperties = {
  fontSize: 14,
  color: "var(--color-text-tertiary)",
  flexShrink: 0,
};

const titleStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  minWidth: 0,
};

const metaStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  whiteSpace: "nowrap",
};

const timeStyle: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
  marginLeft: "auto",
};

const openStyle: CSSProperties = {
  color: "var(--color-text-tertiary)",
  display: "flex",
  alignItems: "center",
};

const showAllStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  padding: "8px 8px 0",
  cursor: "default",
};
